"use client";
/**
 * The parts five surfaces share, so one movement is not described five ways.
 *
 * Every piece here reads the same model helpers the row, the three cards, the
 * rail card, the detail and the plan read. The vendor's own vocabulary arrives
 * in three casings on one record — a screamed `YOGA`, a snake-cased
 * `full_body`, a lowercase `bodyweight` — and it is normalised in exactly one
 * place (`cworkoutcardVendorWords`) so a card cannot spell a muscle one way
 * while the row above it spells it another.
 */
import type { CSSProperties, ReactElement } from "react";
import {
  CresultcardChipRow,
  CresultcardFactLine,
  CresultcardFocal,
  CresultcardListRow,
  CresultcardNotice,
  CresultcardProse,
  CresultcardStack,
  CresultcardSubline,
  CRC_TEXT_FAINT,
  CRC_TYPE_STYLE,
  CresultcardAlign,
  CresultcardChipRegister,
  CresultcardChipVariant,
  CresultcardFigureScale,
  CresultcardProseMode,
  CresultcardProseVisible,
  CresultcardStackDensity,
  CresultcardSublineSize,
  CresultcardTextStep,
  CresultcardTitleLines,
  CresultcardTone,
  type CresultcardChipSpec,
  type CresultcardFactLineEntry,
} from "@/components/ui/Cresultcard";
import {
  CWORKOUTCARD_FEWER_STEPS_LABEL,
  CWORKOUTCARD_MORE_STEPS_LABEL,
  CWORKOUTCARD_PREMIUM_LABEL,
  CWORKOUTCARD_SECONDARY_MUSCLES_LABEL,
  CWORKOUTCARD_SEE_ALL_TYPES_LABEL,
  CWORKOUTCARD_SETS_LABEL,
  CWORKOUTCARD_STEPS_NOUN,
  CWORKOUTCARD_STEPS_WITHHELD_SUFFIX,
  CWORKOUTCARD_STEP_NOUN,
  C_WORKOUT_CATEGORY_CHIP_KEY,
  C_WORKOUT_CHIP_LIMIT,
  C_WORKOUT_MUSCLE_DROP_ORDER,
  C_WORKOUT_PREMIUM_CHIP_KEY,
  C_WORKOUT_ROW_DROP_ORDER,
  C_WORKOUT_SHOOT_LABEL,
  C_WORKOUT_VIDEO_STATE_LABEL,
} from "./cworkoutcard.constants";
import {
  cworkoutcardMoveLines,
  cworkoutcardMuscles,
  cworkoutcardPrescriptionLine,
  cworkoutcardPrimaryClip,
  cworkoutcardVendorWords,
} from "./cworkoutcard.model";
import {
  CworkoutcardMetaSlot,
  CworkoutcardMuscleSlot,
  CworkoutcardVideoState,
  type CworkoutcardData,
  type CworkoutcardMoveReading,
} from "./cworkoutcard.types";

const MUSCLE_SEPARATOR = ", ";
/**
 * The narration's visible window, sized to the REAL longest paragraph rather
 * than to a round number.
 *
 * The measure is 62 characters — the kit's own, and the widest a line of prose
 * gets in this family — and the longest captured description is 349 characters
 * (a generated plan slot; the catalog rows run 238 to 270). 349 ÷ 62 = 5.63, so
 * six lines is what shows every real paragraph WHOLE at the detail's measure.
 *
 * On a CARD the container is narrower than the measure — a two-up cell is about
 * 250px, roughly 35 characters — so six lines hold about 210 characters and the
 * longest paragraph still reaches the disclosure. That is stated rather than
 * hidden: the card's DEFAULT reading is the numbered steps, whose longest line
 * is 77 characters and fits a card at three lines without an ellipsis, and the
 * narration is the alternate reading a reader chooses knowing it is long.
 */
const NARRATION_LINES = 6;
const NARRATION_MEASURE = 62;

const CAPTION_STYLE: CSSProperties = {
  ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
  color: CRC_TEXT_FAINT,
};

/** The tone each non-playable state is stated in. Only one of them is a fault. */
const STATE_TONE: Record<CworkoutcardVideoState, CresultcardTone> = {
  [CworkoutcardVideoState.Playable]: CresultcardTone.Neutral,
  [CworkoutcardVideoState.Expired]: CresultcardTone.Caution,
  [CworkoutcardVideoState.Unresolved]: CresultcardTone.Muted,
  [CworkoutcardVideoState.Absent]: CresultcardTone.Muted,
  [CworkoutcardVideoState.Failed]: CresultcardTone.Negative,
};

interface CworkoutcardMetaProps {
  readonly data: CworkoutcardData;
}

/**
 * The row's second line: the muscle, the equipment and the difficulty, dropped
 * from the tail in the family's declared order when the measure runs out.
 *
 * The muscle never drops. It is the field a catalog page is scanned by, and it
 * is the one of the three that differs on every captured row — `bodyweight`
 * and `beginner` are identical across all three rows of the mounted page.
 */
export function CworkoutcardMetaLine({
  data,
}: CworkoutcardMetaProps): ReactElement {
  const facts: readonly CresultcardFactLineEntry[] = [
    {
      key: CworkoutcardMetaSlot.Muscle,
      value: cworkoutcardVendorWords(data.muscleGroup),
    },
    {
      key: CworkoutcardMetaSlot.Equipment,
      value: cworkoutcardVendorWords(data.equipment),
    },
    {
      key: CworkoutcardMetaSlot.Difficulty,
      value: cworkoutcardVendorWords(data.difficulty),
    },
  ];
  return (
    <CresultcardFactLine
      dropOrder={C_WORKOUT_ROW_DROP_ORDER}
      facts={facts}
    />
  );
}

/**
 * The exercise types, plus the catalog's own category and its premium flag.
 *
 * Every captured record carries exactly three types, so the limit is a ceiling
 * for a record that carries more rather than a promise about this page.
 */
export function CworkoutcardTypeChips({
  data,
}: CworkoutcardMetaProps): ReactElement | null {
  const chips: readonly CresultcardChipSpec[] = [
    ...(data.category == null
      ? []
      : [
          {
            key: C_WORKOUT_CATEGORY_CHIP_KEY,
            label: cworkoutcardVendorWords(data.category),
            variant: CresultcardChipVariant.Accent,
          },
        ]),
    ...data.exerciseTypes.map((type) => ({
      key: type,
      label: cworkoutcardVendorWords(type),
    })),
    ...(data.isPremium === true
      ? [
          {
            key: C_WORKOUT_PREMIUM_CHIP_KEY,
            label: CWORKOUTCARD_PREMIUM_LABEL,
            variant: CresultcardChipVariant.Caution,
          },
        ]
      : []),
  ];
  if (chips.length === 0) {
    return null;
  }
  return (
    <CresultcardChipRow
      chips={chips}
      limit={C_WORKOUT_CHIP_LIMIT}
      register={CresultcardChipRegister.Attribute}
      seeAllLabel={CWORKOUTCARD_SEE_ALL_TYPES_LABEL}
      wrap
    />
  );
}

/**
 * What the movement works: the primary muscle, then the secondaries under the
 * vendor's own relationship word.
 *
 * Only one of the four captured records carries secondaries at all, so the
 * clause is drawn only where there is something to put in it — an `also works`
 * label over nothing states an absence the record never claimed.
 *
 * It is a FACT LINE rather than a subline, and the reason is measurement. The
 * kit's subline is one line with an ellipsis built in, and the real worst case
 * here is `Glutes · also works Hamstrings, Erector spinae, Core` — 52
 * characters, which ellipsises inside a 250px card body. The fact line measures
 * its own width and drops the secondaries WHOLE when they do not fit, so the
 * primary muscle — the field a catalog page is scanned by — is never the half
 * that gets cut.
 */
export function CworkoutcardMuscleLine({
  data,
}: CworkoutcardMetaProps): ReactElement | null {
  const muscles = cworkoutcardMuscles(data);
  const [primary, ...secondary] = muscles;
  if (primary == null) {
    return null;
  }
  const facts: readonly CresultcardFactLineEntry[] = [
    { key: CworkoutcardMetaSlot.Muscle, value: primary },
    ...(secondary.length === 0
      ? []
      : [
          {
            key: CworkoutcardMuscleSlot.Secondary,
            value: `${CWORKOUTCARD_SECONDARY_MUSCLES_LABEL} ${secondary.join(MUSCLE_SEPARATOR)}`,
          },
        ]),
  ];
  return (
    <CresultcardFactLine
      dropOrder={C_WORKOUT_MUSCLE_DROP_ORDER}
      facts={facts}
    />
  );
}

interface CworkoutcardPrescriptionProps extends CworkoutcardMetaProps {
  readonly scale: CresultcardFigureScale;
  readonly align?: CresultcardAlign;
}

/**
 * The plan's own prescription as the surface's headline figure.
 *
 * `sets` is the number and `repetitions` is its unit, because the vendor sends
 * repetitions as a STRING and both of its shapes arrive on one plan: `8-12` on
 * a main lift and `30 seconds` on a warm-up hold. Parsing either into a number
 * would make the hold read as thirty repetitions.
 */
export function CworkoutcardPrescription({
  data,
  scale,
  align = CresultcardAlign.End,
}: CworkoutcardPrescriptionProps): ReactElement | null {
  const { prescription } = data;
  if (prescription == null) {
    return null;
  }
  return (
    <CresultcardFocal
      align={align}
      scale={scale}
      support={cworkoutcardPrescriptionLine(prescription)}
      title={CWORKOUTCARD_SETS_LABEL}
      unit={prescription.repetitions}
      value={String(prescription.sets)}
    />
  );
}

interface CworkoutcardMoveProps extends CworkoutcardMetaProps {
  readonly reading: CworkoutcardMoveReading;
  /** How many steps a bounded card shows before the disclosure. */
  readonly limit?: number;
}

/**
 * The movement itself, in whichever register is selected.
 *
 * A narration is one paragraph and renders through the kit's reading prose, so
 * the four visible lines are the kit's own measure rather than a clamp this
 * family invented. Steps render as numbered list rows, which is what a person
 * mid-set actually reads. Where the selected reading has no field on this
 * record — a plan slot carries the paragraph and no steps — the other field is
 * shown, as the one paragraph it is, rather than the card going blank.
 */
export function CworkoutcardMove({
  data,
  reading,
  limit,
}: CworkoutcardMoveProps): ReactElement | null {
  const lines = cworkoutcardMoveLines({ data, reading });
  if (lines.length === 0) {
    return null;
  }
  const [narration] = lines;
  if (lines.length === 1 && narration != null) {
    return (
      <CresultcardProse
        collapseLabel={CWORKOUTCARD_FEWER_STEPS_LABEL}
        expandLabel={CWORKOUTCARD_MORE_STEPS_LABEL}
        lines={NARRATION_LINES}
        measure={NARRATION_MEASURE}
        mode={CresultcardProseMode.Reading}
        text={narration}
        visible={CresultcardProseVisible.Lines}
      />
    );
  }
  const shown = limit == null ? lines : lines.slice(0, limit);
  const withheld = lines.length - shown.length;
  return (
    <CresultcardStack
      density={CresultcardStackDensity.Tight}
    >
      {shown.map((line, index) => (
        <CresultcardListRow
          key={line}
          onLast={index === shown.length - 1 && withheld === 0}
          ordinal={index + 1}
          title={line}
          titleLines={CresultcardTitleLines.Three}
        />
      ))}
      {withheld > 0 && (
        <span
          style={CAPTION_STYLE}
        >
          {`${String(withheld)} more ${withheld === 1 ? CWORKOUTCARD_STEP_NOUN : CWORKOUTCARD_STEPS_NOUN} ${CWORKOUTCARD_STEPS_WITHHELD_SUFFIX}`}
        </span>
      )}
    </CresultcardStack>
  );
}

interface CworkoutcardVideoCaptionProps {
  readonly data: CworkoutcardData;
  readonly state: CworkoutcardVideoState;
}

/**
 * What the frame above it could not say for itself: which shoot the clip came
 * off, and — for the two states a reader can still act on — which one it is in.
 *
 * `Playable` gets a quiet line and no notice, because a working video does not
 * need to be explained. `Expired` and `Failed` get the kit's notice in their own
 * tone. A movement nobody filmed and a catalog row with no address get nothing
 * at all: there is no clip there, and the empty frame is already the whole fact.
 */
export function CworkoutcardVideoCaption({
  data,
  state,
}: CworkoutcardVideoCaptionProps): ReactElement | null {
  const clip = cworkoutcardPrimaryClip(data.media);
  const shoot = clip?.shoot ?? null;

  if (state === CworkoutcardVideoState.Playable) {
    return shoot == null ? null : (
      <CresultcardSubline
        size={CresultcardSublineSize.Small}
      >
        {C_WORKOUT_SHOOT_LABEL[shoot]}
      </CresultcardSubline>
    );
  }

  const label = C_WORKOUT_VIDEO_STATE_LABEL[state];
  if (label == null) {
    return null;
  }

  return <CresultcardNotice tone={STATE_TONE[state]}>{label}</CresultcardNotice>;
}
